import type { IncomeDashboardDetail } from '../api/incomeVerificationDashboardApi';

export type IncomeDepositPanelData = Pick<
  IncomeDashboardDetail,
  'id' | 'finalStatus' | 'verdict' | 'metrics' | 'incomeDeposits' | 'bankTransactions' | 'transactionMeta'
>;

function cell(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '—';
}

export function parseIncomeDepositPanelData(payload: unknown): IncomeDepositPanelData | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const o = payload as Record<string, unknown>;
  if (typeof o.id !== 'string') return null;
  return payload as IncomeDepositPanelData;
}

export function parseJourneyIncomeDeposits(
  sections: Record<string, unknown>,
): IncomeDashboardDetail['incomeDeposits'] {
  const raw = sections.incomeDeposits;
  if (!Array.isArray(raw)) return [];
  return raw as IncomeDashboardDetail['incomeDeposits'];
}

export function buildDepositPanelDataFromJourney(
  highlightIncomeRecordId: string | null | undefined,
  incomeOutcomes: Record<string, unknown>[],
  incomeDeposits: IncomeDashboardDetail['incomeDeposits'],
): IncomeDepositPanelData | null {
  if (incomeDeposits.length === 0) return null;
  const row =
    (highlightIncomeRecordId
      ? incomeOutcomes.find((r) => cell(r.id) === highlightIncomeRecordId)
      : null) ?? incomeOutcomes[incomeOutcomes.length - 1];
  if (!row) return null;
  const metrics = row.metrics as Record<string, unknown> | undefined;
  return {
    id: cell(row.id),
    finalStatus: cell(row.finalStatus),
    verdict: cell(row.verdict) !== '—' ? cell(row.verdict) : null,
    metrics: metrics
      ? {
          normalizedMonthlyIncome:
            typeof metrics.normalizedMonthlyIncome === 'number'
              ? metrics.normalizedMonthlyIncome
              : Number(metrics.normalizedMonthlyIncome) || null,
          stabilityScore: null,
          volatilityIndex: null,
          recurrenceConfidence: null,
          frequencyClassification:
            typeof metrics.frequencyClassification === 'string' ? metrics.frequencyClassification : null,
          primaryPayor: typeof metrics.primaryPayor === 'string' ? metrics.primaryPayor : null,
          incomeClass: typeof metrics.incomeClass === 'string' ? metrics.incomeClass : null,
          incomeType: typeof metrics.incomeType === 'string' ? metrics.incomeType : null,
          depositsUsed: typeof metrics.depositsUsed === 'number' ? metrics.depositsUsed : 0,
          depositsExcluded: typeof metrics.depositsExcluded === 'number' ? metrics.depositsExcluded : 0,
        }
      : null,
    incomeDeposits,
    bankTransactions: [],
    transactionMeta: {
      incomeDepositsReturned: incomeDeposits.length,
      incomeDepositsCapped: false,
      bankTransactionsReturned: 0,
      bankTransactionsCapped: false,
    },
  };
}
